"use client";

import { filter, find, map } from "lodash";
import { useLocale } from "next-intl";
import { useMemo } from "react";

import { GetUndressingsQuery } from "@/.gql/graphql";
import { useAiRequestInfo } from "@/components/hooks/useAiRequestInfo";

/**
 * Хук для получения и подготовки данных сторилайна
 *
 * @description
 * Извлекает изображения и тексты из данных раздевания.
 * Поддерживает локализацию текстов.
 *
 * @param undressings - Массив результатов раздевания
 * @returns Объект с массивами изображений и текстов
 *
 * @example
 * ```tsx
 * const { images, texts } = useStorylineData(undressings);
 * ```
 */
export const useStorylineData = (
  undressings: GetUndressingsQuery["getUndressings"],
) => {
  const locale = useLocale();
  const { findCategoryById, getAiRequestById } = useAiRequestInfo();

  const images = useMemo(
    () =>
      filter(
        map(undressings, (file) => file.fileUrl),
        Boolean,
      ),
    [undressings],
  );

  const texts = useMemo(
    () =>
      map(undressings, ({ aiRequestIds }) => {
        const storylineId = find(
          aiRequestIds,
          (id) => findCategoryById(id) === "storylines",
        );

        if (!storylineId) {
          return null;
        }

        const request = getAiRequestById(storylineId);
        return request?.storyline[locale] || request?.storyline["en"] || null;
      }),
    [findCategoryById, getAiRequestById, locale, undressings],
  );

  return { images, texts };
};
